import React, { useState } from 'react';
import { ClinicalContext, CardiacPhenotype, CARDIAC_PHENOTYPE_LABELS, EMPTY_CLINICAL_CONTEXT } from '../types';

interface ClinicalContextInputProps {
  clinicalContext: ClinicalContext;
  onChange: (context: ClinicalContext) => void;
  disabled?: boolean;
}

const PHENOTYPE_OPTIONS: { value: CardiacPhenotype; label: string }[] = Object.entries(CARDIAC_PHENOTYPE_LABELS).map(
  ([value, label]) => ({ value: value as CardiacPhenotype, label })
);

const ClinicalContextInput: React.FC<ClinicalContextInputProps> = ({ clinicalContext, onChange, disabled }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAdditional, setShowAdditional] = useState(false);

  const update = (patch: Partial<ClinicalContext>) => {
    onChange({ ...clinicalContext, ...patch });
  };

  const hasAnyContext = Object.values(clinicalContext).some(v => v !== null && v !== '');

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange({ ...EMPTY_CLINICAL_CONTEXT });
    setShowAdditional(false);
  };

  return (
    <div className="w-full max-w-2xl mt-3">
      <div
        className={`bg-white border ${isExpanded ? 'border-slate-300' : 'border-slate-200'} rounded-2xl overflow-hidden transition-all shadow-sm`}
      >
        {/* Header */}
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-slate-50 transition-colors"
          disabled={disabled}
        >
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">🏥</span>
            <span className="text-sm font-medium text-slate-600">
              Clinical Context
            </span>
            <span className="text-xs text-slate-400">(Optional)</span>
            {hasAnyContext && !isExpanded && (
              <span className="ml-1 w-2 h-2 rounded-full bg-emerald-400 inline-block" title="Context provided" />
            )}
          </div>
          <span className="text-xs text-slate-400">{isExpanded ? '▼' : '▶'}</span>
        </button>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="px-5 pb-5 border-t border-slate-100 space-y-4">
            {/* Row 1: Phenotype, Age, Sex */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4">
              {/* Phenotype */}
              <div className="sm:col-span-1">
                <label className="block text-xs font-medium text-slate-500 mb-1">Phenotype</label>
                <select
                  value={clinicalContext.phenotype || ''}
                  onChange={(e) => update({ phenotype: (e.target.value || null) as CardiacPhenotype | null })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10"
                  disabled={disabled}
                >
                  <option value="">Select...</option>
                  {PHENOTYPE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Age */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Age</label>
                <input
                  type="number"
                  min={0}
                  max={120}
                  value={clinicalContext.age ?? ''}
                  onChange={(e) => update({ age: e.target.value ? parseInt(e.target.value, 10) : null })}
                  placeholder="—"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10"
                  disabled={disabled}
                />
              </div>

              {/* Sex */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Sex</label>
                <div className="flex gap-1">
                  {(['male', 'female'] as const).map(sex => (
                    <button
                      key={sex}
                      type="button"
                      onClick={() => update({ sex: clinicalContext.sex === sex ? null : sex })}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                        clinicalContext.sex === sex
                          ? 'bg-brand-50 border-brand-300 text-brand-700 font-medium'
                          : 'border-slate-200 text-slate-500 hover:border-slate-300'
                      }`}
                      disabled={disabled}
                    >
                      {sex === 'male' ? 'M' : 'F'}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Family History of SCD */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Family History of SCD</label>
              <div className="flex gap-1">
                {(['yes', 'no', 'unknown'] as const).map(val => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => update({ familyHistorySCD: clinicalContext.familyHistorySCD === val ? null : val })}
                    className={`px-4 py-1.5 text-sm rounded-lg border transition-colors ${
                      clinicalContext.familyHistorySCD === val
                        ? val === 'yes'
                          ? 'bg-red-50 border-red-300 text-red-700 font-medium'
                          : 'bg-brand-50 border-brand-300 text-brand-700 font-medium'
                        : 'border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                    disabled={disabled}
                  >
                    {val.charAt(0).toUpperCase() + val.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Additional Findings Toggle */}
            <button
              type="button"
              onClick={() => setShowAdditional(!showAdditional)}
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors flex items-center gap-1"
            >
              <span>{showAdditional ? '▼' : '▶'}</span>
              Additional Findings
            </button>

            {/* Additional Findings */}
            {showAdditional && (
              <div className="space-y-3 pl-0">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Echo Findings</label>
                  <input
                    type="text"
                    value={clinicalContext.echoFindings || ''}
                    onChange={(e) => update({ echoFindings: e.target.value || null })}
                    placeholder="e.g., IVSd 18mm, EF 55%, SAM present"
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 placeholder-slate-300 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10"
                    disabled={disabled}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">ECG Findings</label>
                  <input
                    type="text"
                    value={clinicalContext.ecgFindings || ''}
                    onChange={(e) => update({ ecgFindings: e.target.value || null })}
                    placeholder="e.g., LVH voltage criteria, TWI V4-V6"
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 placeholder-slate-300 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10"
                    disabled={disabled}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Symptoms</label>
                  <input
                    type="text"
                    value={clinicalContext.symptoms || ''}
                    onChange={(e) => update({ symptoms: e.target.value || null })}
                    placeholder="e.g., exertional syncope, palpitations"
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 placeholder-slate-300 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10"
                    disabled={disabled}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Prior Genetic Testing</label>
                  <input
                    type="text"
                    value={clinicalContext.priorGeneticTesting || ''}
                    onChange={(e) => update({ priorGeneticTesting: e.target.value || null })}
                    placeholder="e.g., 56-gene panel negative, 2023"
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 placeholder-slate-300 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10"
                    disabled={disabled}
                  />
                </div>
              </div>
            )}

            {/* Clear */}
            {hasAnyContext && (
              <div className="flex justify-end pt-1">
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-xs text-slate-400 hover:text-red-500 transition-colors"
                  disabled={disabled}
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClinicalContextInput;
